import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, X, Loader2, Check, ArrowRight } from 'lucide-react';
import {
  isPushSupported,
  isPushEnabled,
  enablePushNotifications,
} from '../lib/pushNotifications';

const DISMISSED_KEY = 'bchnostr:push-banner-dismissed';

function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const mq = window.matchMedia?.('(display-mode: standalone)')?.matches === true;
  return iosStandalone || mq;
}

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

interface EnablePushBannerProps {
  pubkey: string;
}

/**
 * A dismissible feed banner that nudges logged-in users to turn on push
 * notifications — a big retention lever, since it pulls people back to the
 * app for zaps, replies, and mentions even when it's closed.
 *
 * It enables push in a single tap right from the banner (no trip to Settings),
 * and gracefully handles the cases where push is unsupported, already on, or
 * blocked by iOS's "must install to Home Screen" restriction.
 */
export default function EnablePushBanner({ pubkey }: EnablePushBannerProps) {
  const [supported] = useState(() => isPushSupported());
  const [enabled, setEnabled] = useState(() => isPushEnabled());
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justEnabled, setJustEnabled] = useState(false);

  // On iOS, Web Push only works when the site is installed to the Home Screen.
  const iosNotInstalled = isIOSDevice() && !isStandalonePWA();

  // Don't show if: unsupported, already enabled, dismissed, or the user
  // previously denied the OS permission (respect their choice).
  const permissionDenied =
    typeof Notification !== 'undefined' && Notification.permission === 'denied';

  if (!supported || enabled || dismissed || permissionDenied) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  };

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await enablePushNotifications(pubkey);
      if (res.ok) {
        setEnabled(true);
        setJustEnabled(true);
      } else {
        setError(res.error || 'Could not enable notifications.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  // Brief success confirmation before the banner disappears.
  if (justEnabled) {
    return (
      <div className="mx-3 mb-2 flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5" style={{ minHeight: 52 }}>
        <Check className="w-5 h-5 text-emerald-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">You're all set!</p>
          <p className="text-xs text-muted-foreground">We'll ping you about zaps, replies, and mentions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-3 mb-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5">
      <div className="flex items-center gap-3" style={{ minHeight: 40 }}>
        <BellRing className="w-5 h-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Never miss a zap</p>
          <p className="text-xs text-muted-foreground">
            Turn on notifications for replies, mentions &amp; zaps — even when BCH Nostr is closed.
          </p>
        </div>

        {!iosNotInstalled && (
          <button
            onClick={handleEnable}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {busy ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enabling…</>
            ) : (
              <>Turn on <ArrowRight className="w-3.5 h-3.5" /></>
            )}
          </button>
        )}

        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* iOS: can't enable from a browser tab — guide them to install first. */}
      {iosNotInstalled && (
        <p className="text-[11px] text-muted-foreground mt-2 bg-muted/50 rounded-lg px-3 py-2">
          On iPhone/iPad, add BCH Nostr to your Home Screen (Share → “Add to Home
          Screen”), open it from there, then{' '}
          <Link to="/notifications" className="text-primary font-medium underline">
            enable notifications
          </Link>
          .
        </p>
      )}

      {error && (
        <p className="text-[11px] text-red-500 mt-2 bg-red-500/10 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
    </div>
  );
}
